import { useCallback, useEffect, useRef, useState } from "react";
import { adminCreateGallery, adminDeleteGallery, adminFetchGallery, adminUpdateGallery, adminUploadGalleryImage, adminScanUploads } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Edit2, Trash2, Plus, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { url: "", caption: "", category: "burgers", sort_order: 0 };

const CATEGORIES = [
    { value: "burgers",  label: "Burgers & Sandwiches" },
    { value: "wings",    label: "Wings & Chicken" },
    { value: "plates",   label: "Plates & Sides" },
    { value: "smoker",   label: "From the Pit" },
    { value: "truck",    label: "The Truck" },
    { value: "awards",   label: "Awards & Events" },
];

function UploadZone({ onUploaded }) {
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [queue, setQueue] = useState([]);
    const inputRef = useRef(null);

    const processFiles = useCallback(async (files) => {
        const images = Array.from(files).filter((f) => f.type.startsWith("image/"));
        if (!images.length) { toast.error("No image files found."); return; }
        setUploading(true);
        setQueue(images.map((f) => ({ name: f.name, done: false, error: false })));
        const results = [];
        for (let i = 0; i < images.length; i++) {
            try {
                const result = await adminUploadGalleryImage(images[i], "", "food", i);
                results.push(result);
                setQueue((q) => q.map((x, idx) => idx === i ? { ...x, done: true } : x));
            } catch {
                setQueue((q) => q.map((x, idx) => idx === i ? { ...x, error: true } : x));
            }
        }
        setUploading(false);
        if (results.length) {
            toast.success(`Uploaded ${results.length} of ${images.length} photo${images.length !== 1 ? "s" : ""}.`);
            onUploaded(results);
        }
        setTimeout(() => setQueue([]), 3000);
    }, [onUploaded]);

    const onDrop = useCallback((e) => {
        e.preventDefault();
        setDragging(false);
        processFiles(e.dataTransfer.files);
    }, [processFiles]);

    return (
        <div className="space-y-4">
            <div
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${dragging ? "border-orange-400 bg-orange-400/5" : "border-white/20 hover:border-white/40"}`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
            >
                <Upload className="mx-auto mb-3 text-orange-300/70" size={32} />
                <p className="text-white/80 font-medium">Drag &amp; drop photos here</p>
                <p className="text-white/40 text-sm mt-1">or click to browse — multiple files OK</p>
                <p className="text-white/30 text-xs mt-2">JPEG · PNG · WebP · GIF</p>
                <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => processFiles(e.target.files)} />
            </div>
            {queue.length > 0 && (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                    {queue.map((item, i) => (
                        <div key={i} className={`flex items-center justify-between px-3 py-1.5 rounded-md text-sm ${item.error ? "bg-red-500/10 text-red-300" : item.done ? "bg-green-500/10 text-green-300" : "bg-white/5 text-white/60"}`}>
                            <span className="truncate max-w-[220px]">{item.name}</span>
                            <span className="text-xs ml-2 shrink-0">{item.error ? "failed" : item.done ? "done" : uploading ? "uploading…" : "queued"}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminGalleryPage() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [scanning, setScanning] = useState(false);

    const load = async () => setItems(await adminFetchGallery());
    useEffect(() => { load().catch(() => {}); }, []);

    const scanUploads = async () => {
        setScanning(true);
        try {
            const result = await adminScanUploads();
            if (result.imported > 0) {
                toast.success(`Imported ${result.imported} new photo${result.imported !== 1 ? 's' : ''} from uploads folder.`);
                await load();
            } else {
                toast.info('No new photos found in uploads folder.');
            }
        } catch { toast.error('Scan failed'); }
        finally { setScanning(false); }
    };

    const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
    const startEdit = (g) => {
        setEditing(g);
        setForm({ url: g.url || "", caption: g.caption || "", category: g.category || "food", sort_order: g.sort_order || 0 });
        setOpen(true);
    };
    const save = async () => {
        if (!form.url) { toast.error("Image URL required."); return; }
        setSaving(true);
        try {
            if (editing) {
                const updated = await adminUpdateGallery(editing.id, form);
                setItems((p) => p.map((x) => x.id === editing.id ? updated : x));
                toast.success("Image updated.");
            } else {
                const created = await adminCreateGallery(form);
                setItems((p) => [created, ...p]);
                toast.success("Image added.");
            }
            setOpen(false);
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this image?")) return;
        await adminDeleteGallery(id);
        setItems((p) => p.filter((x) => x.id !== id));
        toast.success("Deleted.");
    };

    const handleUploaded = (newItems) => {
        setItems((prev) => [...newItems, ...prev]);
        setUploadOpen(false);
    };

    const imageUrl = (url) => {
        if (!url) return "";
        if (url.startsWith("/uploads/")) {
            const base = process.env.REACT_APP_BACKEND_URL || "";
            return `${base}${url}`;
        }
        return url;
    };

    return (
        <div data-testid="admin-gallery-manager">
            <div className="flex items-end justify-between mb-6 gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.32em] text-orange-300/90">Admin</div>
                    <h1 className="font-display text-3xl tracking-[0.05em] text-white">Gallery</h1>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                    <button
                        onClick={scanUploads}
                        disabled={scanning}
                        className="px-4 py-2 rounded-md border border-white/20 text-sm font-display tracking-[0.12em] uppercase flex items-center gap-1 text-white/60 hover:text-white hover:border-white/40"
                        title="Register any image files dropped directly into backend/uploads/"
                    >
                        {scanning ? "Scanning…" : "Scan Uploads Folder"}
                    </button>
                    <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                        <DialogTrigger asChild>
                            <button className="ember-btn px-4 py-2 rounded-md text-sm font-display tracking-[0.12em] uppercase flex items-center gap-1"><Upload size={14}/> Upload Photos</button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg bg-[#0a0a0b] border border-white/10 text-white">
                            <DialogHeader><DialogTitle>Upload Photos</DialogTitle></DialogHeader>
                            <UploadZone onUploaded={handleUploaded} />
                            <DialogFooter>
                                <button className="px-4 py-2 rounded-md border border-white/10 text-white/80" onClick={() => setUploadOpen(false)}>Close</button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button className="px-4 py-2 rounded-md border border-white/20 text-sm font-display tracking-[0.12em] uppercase flex items-center gap-1 text-white/70 hover:text-white hover:border-white/40" onClick={startCreate} data-testid="admin-gallery-add-button"><Plus size={14}/> Add by URL</button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg bg-[#0a0a0b] border border-white/10 text-white" data-testid="admin-gallery-form-dialog">
                            <DialogHeader><DialogTitle>{editing ? "Edit Image" : "Add Image"}</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-white/80">Image URL</Label>
                                    <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-gallery-form-url" />
                                </div>
                                <div>
                                    <Label className="text-white/80">Caption</Label>
                                    <Input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-gallery-form-caption" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-white/80">Category</Label>
                                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                            <SelectTrigger className="mt-1 bg-black/40 border-white/10" data-testid="admin-gallery-form-category"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-white/80">Sort order</Label>
                                        <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-gallery-form-sort" />
                                    </div>
                                </div>
                                {form.url && (
                                    <img src={imageUrl(form.url)} alt="preview" className="w-full max-h-64 object-cover rounded-md border border-white/10" />
                                )}
                            </div>
                            <DialogFooter>
                                <button className="px-4 py-2 rounded-md border border-white/10 text-white/80" onClick={() => setOpen(false)} data-testid="admin-gallery-form-cancel">Cancel</button>
                                <button className="ember-btn px-4 py-2 rounded-md text-sm font-display tracking-[0.12em] uppercase" onClick={save} disabled={saving} data-testid="admin-gallery-form-save">{saving ? "Saving..." : "Save"}</button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-gallery-grid">
                {items.map((g) => (
                    <Card key={g.id} className="surface-card border-0 overflow-hidden" data-testid="admin-gallery-row">
                        <CardContent className="p-0">
                            {g.url ? (
                                <img src={imageUrl(g.url)} alt={g.caption || ""} className="w-full h-40 object-cover" />
                            ) : (
                                <div className="w-full h-40 bg-white/5 flex items-center justify-center"><ImageIcon className="text-white/20" size={32} /></div>
                            )}
                            <div className="p-3">
                                <div className="text-sm text-white/85 line-clamp-1">{g.caption || "—"}</div>
                                <div className="text-[10px] uppercase tracking-[0.28em] text-orange-300/90">{CATEGORIES.find(c => c.value === g.category)?.label || g.category}</div>
                                <div className="flex items-center justify-end gap-2 mt-2">
                                    <button className="p-2 rounded-md border border-white/10 hover:bg-white/5" onClick={() => startEdit(g)} aria-label="Edit" data-testid="admin-gallery-edit-button"><Edit2 size={14}/></button>
                                    <button className="p-2 rounded-md border border-red-500/20 text-red-300 hover:bg-red-500/10" onClick={() => remove(g.id)} aria-label="Delete" data-testid="admin-gallery-delete-button"><Trash2 size={14}/></button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
                {items.length === 0 && (
                    <div className="col-span-full text-center py-20">
                        <Upload className="mx-auto mb-3 text-white/20" size={40} />
                        <p className="text-white/40">No photos yet — click "Upload Photos" to add Mo's BBQ shots.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
